import React, { useState } from "react";
import { ROLES } from "../roles";
import GameHistoryScreen from "./GameHistoryScreen";
import RoleCardsModal from "./RoleCardsModal";

const STORAGE_KEY = "mafia_host_players_v1";

export function loadStoredPlayers() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function savePlayers(players) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(players));
}

let localIdCounter = 1;
function nextLocalId() {
  return `local-${Date.now()}-${localIdCounter++}`;
}

export default function SetupScreen({ onStart, starting, startError }) {
  // 'setup' — звичний екран розсадки гравців, 'history' — новий розділ
  // історії ігор (поки що заглушка, буде реалізований пізніше — див.
  // GameHistoryPlaceholder нижче).
  const [view, setView] = useState("setup");
  const [players, setPlayers] = useState(() => loadStoredPlayers());
  const [nameDraft, setNameDraft] = useState("");
  const [showRoleCards, setShowRoleCards] = useState(false);

  function persist(next) {
    setPlayers(next);
    savePlayers(next);
  }

  function addPlayer() {
    const name = nameDraft.trim();
    if (!name) return;
    const next = [...players, { id: nextLocalId(), name, role: ROLES[0].id }];
    persist(next);
    setNameDraft("");
  }

  function removePlayer(id) {
    persist(players.filter((p) => p.id !== id));
  }

  function setRole(id, role) {
    persist(players.map((p) => (p.id === id ? { ...p, role } : p)));
  }

  function handleKeyDown(e) {
    if (e.key === "Enter") addPlayer();
  }

  const roleCounts = players.reduce((acc, p) => {
    acc[p.role] = (acc[p.role] || 0) + 1;
    return acc;
  }, {});

  const canStart = players.length >= 3;

  return (
    <div className="screen setup-screen">
      <nav className="menu-tabs">
        <button
          type="button"
          className={`btn btn-toggle ${view === "setup" ? "btn-toggle-active" : ""}`}
          onClick={() => setView("setup")}
        >
          Нова гра
        </button>
        <button
          type="button"
          className={`btn btn-toggle ${view === "history" ? "btn-toggle-active" : ""}`}
          onClick={() => setView("history")}
        >
          Історія ігор
        </button>
      </nav>

      {view === "history" ? (
        <GameHistoryScreen />
      ) : (
        <>
      <header className="setup-header">
        <p className="eyebrow-mark">Ведучий</p>
        <h1>Розсадіть гравців за стіл</h1>
        <p className="lede">
          Список гравців зберігається у цьому браузері, тож наступного разу не доведеться
          вводити імена заново. Призначте ролі та починайте гру.
        </p>
      </header>

      <div className="add-player-row">
        <input
          className="text-input"
          placeholder="Ім'я гравця"
          value={nameDraft}
          onChange={(e) => setNameDraft(e.target.value)}
          onKeyDown={handleKeyDown}
        />
        <button className="btn btn-accent" onClick={addPlayer} disabled={!nameDraft.trim()}>
          Додати
        </button>
      </div>

      {players.length === 0 ? (
        <div className="empty-state">
          <p>Ще нікого немає за столом. Додайте першого гравця вище.</p>
        </div>
      ) : (
        <ul className="player-setup-list">
          {players.map((p) => (
            <li key={p.id} className="player-setup-row">
              <span className="player-setup-name">{p.name}</span>
              <select
                className="role-select"
                value={p.role}
                onChange={(e) => setRole(p.id, e.target.value)}
              >
                {ROLES.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.label}
                  </option>
                ))}
              </select>
              <button
                className="btn btn-ghost btn-small"
                onClick={() => removePlayer(p.id)}
                aria-label={`Прибрати ${p.name}`}
              >
                Прибрати
              </button>
            </li>
          ))}
        </ul>
      )}

      {players.length > 0 && (
        <div className="role-summary">
          {ROLES.filter((r) => roleCounts[r.id]).map((r) => (
            <span key={r.id} className={`role-chip role-chip-${r.side}`}>
              {r.label} × {roleCounts[r.id]}
            </span>
          ))}
        </div>
      )}

      <footer className="setup-footer">
        {!canStart && (
          <p className="hint-text">Потрібно щонайменше 3 гравці, щоб почати гру.</p>
        )}
        {startError && <p className="error-text">{startError}</p>}
        <div className="setup-actions">
          {players.length > 0 && (
            <button
              type="button"
              className="btn btn-ghost btn-large"
              onClick={() => setShowRoleCards(true)}
            >
              🎴 Показати картки ролей ({players.length})
            </button>
          )}
          <button
            className="btn btn-primary btn-large"
            disabled={!canStart || starting}
            onClick={() => onStart(players)}
          >
            {starting ? "Створюємо гру…" : "Почати гру"}
          </button>
        </div>
      </footer>
        </>
      )}

      {showRoleCards && (
        <RoleCardsModal players={players} onClose={() => setShowRoleCards(false)} />
      )}
    </div>
  );
}

// Заглушка розділу історії ігор більше не потрібна — реальний екран тепер у
// GameHistoryScreen.js (список зіграних партій + перегляд хронології кожної).
